import { loadAmChartDependencies } from "./Libraries/loadAmChartDependencies";

const zabbixGraphElements = document.querySelectorAll(".zabbixgraph");

let meta = [];

const customBigNumberPrefixes = [
    { "number": 1e+3, "suffix": " <span class='unit'>Kbit/s</span>" },
    { "number": 1e+6, "suffix": " <span class='unit'>Mbit/s</span>" },
    { "number": 1e+9, "suffix": " <span class='unit'>Gbit/s</span>" },
    { "number": 1e+12, "suffix": " <span class='unit'>Tbit/s</span>" },
];

let observer;

let options = {
    root: null,
    rootMargin: "0px",
    threshold: 0
};

observer = new IntersectionObserver(handleIntersect, options);

for (const zabbixGraphElement of zabbixGraphElements) {
    observer.observe(zabbixGraphElement);
}

function handleIntersect(entries, observer) {
    for (const entry of entries) {
        if (entry.isIntersecting) {
            initializeZabbixGraph(entry.target);
            observer.unobserve(entry.target);
        }
    }
}

async function initializeZabbixGraph(graphElement) {
    graphElement.classList.add("loading");

    await loadAmChartDependencies(["core", "charts"]);

    const customNumberFormatter = new am4core.NumberFormatter();
    customNumberFormatter.numberFormat = "#.00a";
    customNumberFormatter.bigNumberPrefixes = customBigNumberPrefixes;

    const configElement = graphElement.querySelector(".zabbixgraph__config");
    const trafficUrl = configElement.dataset.trafficUrl;

    for (const type of ["peak", "current"]) {
        let httpRequest = new XMLHttpRequest();
        httpRequest.onreadystatechange = function () {
            if (httpRequest.readyState === XMLHttpRequest.DONE) {
                if (httpRequest.status === 200) {
                    const response = JSON.parse(httpRequest.responseText);
                    if (response > 0) {
                        let element = graphElement.querySelector(".zabbixgraph__meta-" + type + "> .zabbixgraph__meta-content");
                        let formattedNumber = customNumberFormatter.format(response);
                        element.innerHTML = formattedNumber;
                        meta[type] = formattedNumber;
                    }
                }
            }
        };
        let url = configElement.getAttribute("data-" + type + "-url");
        httpRequest.open("GET", url);
        httpRequest.send();
    }

    const timeFrame = configElement.dataset.timeFrame;
    const colorLine = getComputedStyle(configElement.querySelector(".zabbixgraph__color-line")).color;
    const colorFill = getComputedStyle(configElement.querySelector(".zabbixgraph__color-fill")).color;

    const bigNumberPrefixes = [
        { "number": 1e+3, "suffix": " Kbit/s" },
        { "number": 1e+6, "suffix": " Mbit/s" },
        { "number": 1e+9, "suffix": " Gbit/s" },
        { "number": 1e+12, "suffix": " Tbit/s" },
    ];

    const chartDiv = graphElement.querySelector(".zabbixgraph__chartdiv");
    let chart = am4core.create(chartDiv.id, am4charts.XYChart);
    chart.dataSource.url = trafficUrl;
    chart.dataSource.parser = new am4core.JSONParser();
    chart.dataSource.parser.options = {
        "dateFormat": "x",
        "dateFields": [
            "clock",
        ],
    };
    chart.dateFormatter.utc = true;
    // removed for DECIX-178: chart.dateFormatter.timezone = "Europe/Berlin";
    chart.dataSource.events.on("error", function() {
        const modal = chart.openModal("This chart is currently not available.", "Error loading data");
        modal.closable = false;
        graphElement.classList.remove("loading");
    });
    chart.numberFormatter.numberFormat = "#.a";
    chart.numberFormatter.bigNumberPrefixes = bigNumberPrefixes;

    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.tooltipDateFormat = "HH:mm, d MMMM yyyy zzz";
    dateAxis.groupData = true;

    switch (timeFrame) {
    case "2days":
        dateAxis.groupIntervals.setAll([
            { timeUnit: "minute", count: 10 }
        ]);
        break;
    case "1month":
        dateAxis.groupIntervals.setAll([
            { timeUnit: "hour", count: 1 }
        ]);
        break;
    case "1year":
    case "2years":
        dateAxis.groupIntervals.setAll([
            { timeUnit: "day", count: 1 }
        ]);
        break;
    case "3years":
    case "4years":
    case "5years":
        dateAxis.groupIntervals.setAll([
            { timeUnit: "week", count: 1 }
        ]);
        break;
    default:
        dateAxis.groupIntervals.setAll([
            { timeUnit: "hour", count: 1 }
        ]);
        break;
    }

    let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.numberFormatter.numberFormat = "#.#a";
    // Start y axis at 0
    valueAxis.min = 0;

    let series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.dateX = "clock";
    series.dataFields.valueY = "value";
    series.groupFields.valueY = "high";
    series.tooltipText = "{valueY}";
    series.stroke = am4core.color(colorLine);
    series.fill = am4core.color(colorFill);
    series.fillOpacity = 1;
    series.tensionX = 0.8;
    series.tensionY = 0.8;
    series.minBulletDistance = 15;
    series.tooltip.numberFormatter = new am4core.NumberFormatter();
    series.tooltip.numberFormatter.numberFormat = "#.00a";
    series.tooltip.numberFormatter.bigNumberPrefixes = bigNumberPrefixes;

    const bullet = series.bullets.push(new am4charts.CircleBullet());
    const bulletHover = bullet.states.create("hover");
    bulletHover.properties.scale = 1.3;

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.opacity = 0;
    chart.cursor.behavior = "none";

    chart.dataSource.events.on("done", function(ev) {
        const values = ev.target.data.map(function (object) {
            return object.value;
        });

        const valuesSorted = values.sort((a, b) => a - b);
        const length = valuesSorted.length;

        // Calculate graph peak (highest value in data)
        const graphPeak = valuesSorted[length - 1];
        if (graphPeak) {
            const graphPeakElement = graphElement.querySelector(".zabbixgraph__meta-max > .zabbixgraph__meta-content");
            graphPeakElement.innerHTML = customNumberFormatter.format(graphPeak);
        }

        // Calculate graph average (median)
        const middle = Math.ceil(length / 2);
        const graphMedian = length % 2 === 0 ? (valuesSorted[middle] + valuesSorted[middle - 1]) / 2 : valuesSorted[middle - 1];
        if (graphMedian) {
            const graphAverageElement = graphElement.querySelector(".zabbixgraph__meta-average > .zabbixgraph__meta-content");
            graphAverageElement.innerHTML = customNumberFormatter.format(graphMedian);
        }

        graphElement.classList.remove("loading");
    });
}
